import { useDashboard } from '@/context/DashboardContext';

/*
  Niveles de acceso del panel de miembros (decisión de Samuel, 09/10/2026).

  free     → sin membresía: Inicio (visitante), Oportunidades (vista censurada), Academia, Eventos y Mi perfil.
  access   → miembro: lo anterior + Oportunidades con datos, Mis operaciones, Asistente IA, Mis documentos,
  pro        Comisiones (las suyas de referidor: 10 % del primer pago, con su pipeline de contactos) y su enlace.
  premium  → broker: todo, incluidas las herramientas de broker.
  admin    → todo (el rol «manager» se retiró el 10/10/2026: sus cuentas pasaron a admin).

  Única fuente de verdad en el frontend: el menú, las rutas (AccessGate) y las páginas preguntan
  con can(...). El backend aplica la misma matriz (routes/dashboard.py, _tier).
  La membresía es la efectiva de /api/dashboard/memberships/me (ya descuenta suscripciones caducadas).
*/

export const TIER_BY_PLAN = { starter: 'access', basica: 'pro', premium: 'premium' };

// Qué nivel mínimo pide cada sección
const ALL = ['free', 'access', 'pro', 'premium', 'admin'];
const MEMBERS = ['access', 'pro', 'premium', 'admin'];
const BROKERS = ['premium', 'admin'];

export const FEATURES = {
  home: ALL,
  academy: ALL,
  events: ALL,
  profile: ALL,
  opportunities: MEMBERS, // free la ve en el menú con candado y vista censurada (MembersOnly)
  chat: MEMBERS,
  documents: MEMBERS,
  operations: MEMBERS,   // Mis operaciones (16/10/2026)
  contacts: MEMBERS,     // pipeline de contactos, dentro de Comisiones
  commissions: MEMBERS,
  referral: MEMBERS,
  sales: BROKERS,
  network: BROKERS,
  successKit: BROKERS,
  marketing: BROKERS,
  calculator: BROKERS,
};

// Nivel que pide cada sección, para el mensaje del bloqueo
export function requiredLevel(feature) {
  const allowed = FEATURES[feature] || ALL;
  if (!allowed.includes('free') && !allowed.includes('access')) return 'broker';
  if (!allowed.includes('free')) return 'member';
  return 'free';
}

export const TIER_LABEL = {
  free: 'Free',
  access: 'Access',
  pro: 'PRO',
  premium: 'Broker (Premium)',
  admin: 'Admin',
};

export function tierOf({ user, membership, isAdmin } = {}) {
  if (!user) return 'free';
  if (user.role === 'admin' || isAdmin) return 'admin';
  return TIER_BY_PLAN[membership?.active_membership] || 'free';
}

export function can(tier, feature) {
  return (FEATURES[feature] || ALL).includes(tier);
}

export const isStaffTier = (tier) => tier === 'admin';
export const isBrokerTier = (tier) => BROKERS.includes(tier);
export const isMemberTier = (tier) => MEMBERS.includes(tier);

/** Hook: { tier, can(feature), ready, error, isStaff, isBroker, isMember }.
 *  ready = ya sabemos el nivel (staff, o membresía cargada); mientras no, el menú muestra lo de free. */
export function useAccess() {
  const { user, membership, membershipStatus, isAdmin, refreshMembership } = useDashboard();
  const tier = tierOf({ user, membership, isAdmin });
  const staff = isStaffTier(tier);
  return {
    tier,
    can: (feature) => can(tier, feature),
    ready: staff || membershipStatus === 'ready',
    loading: !staff && (membershipStatus === 'idle' || membershipStatus === 'loading'),
    error: !staff && membershipStatus === 'error',
    refreshMembership,
    isStaff: staff,
    isBroker: isBrokerTier(tier),
    isMember: isMemberTier(tier),
  };
}
